<template>
  <div>
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-primary">{{ icons.arrowLeft }}</v-icon>
        <router-link
          class="text-decoration--none"
          :to="{ name: 'routes.listing' }"
          >Routes</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4" v-if="form">
      <div class="display-2 font-weight-bold my-6 text-capitalize">
        Create Route
      </div>

      <v-form ref="form" v-if="form" class="itinerary-form">
        <v-row class="create-itinerary__content">
          <v-col class="col-md-4 col-sm-12 col-xs-12 mt-6 pa-0">
            <h3 class="py-5 primary--text">Route Information</h3>

            <label class="text-field-label">Name</label>
            <v-text-field
              flat
              solo
              required
              class="mt-2"
              v-model="form.name"
              :error-messages="form.$getError('name')"
              :loading="loading"
            ></v-text-field>

            <label class="text-field-label">Local Information</label>
            <v-textarea
              class="mt-2"
              v-model="form.local_information"
              filled
              auto-grow
              :error-messages="form.$getError('local_information')"
            ></v-textarea>

            <label class="text-field-label">Retail and Restaurants</label>
            <v-autocomplete
              class="mt-2"
              v-model="form.retail_ids"
              :items="retails"
              :loading="fetchingRetails"
              filled
              multiple
              item-text="name"
              item-value="id"
              :error-messages="form.$getError('retail_ids')"
            >
              <template v-slot:append-outer>
                <v-btn
                  color="normal"
                  @click="getRetailPlaces()"
                  :loading="fetchingRetails"
                  icon
                >
                  <v-icon>
                    {{ icons.refresh }}
                  </v-icon>
                </v-btn>
              </template>
            </v-autocomplete>

            <label class="text-field-label">Accommodations</label>
            <v-autocomplete
              class="mt-2"
              v-model="form.accommodation_ids"
              :items="accommodations"
              :loading="fetchingAccommodations"
              filled
              multiple
              item-text="name"
              item-value="id"
              :error-messages="form.$getError('accommodation_ids')"
            >
              <template v-slot:append-outer>
                <v-btn
                  color="normal"
                  @click="getAllAccommodations()"
                  :loading="fetchingAccommodations"
                  icon
                >
                  <v-icon>
                    {{ icons.refresh }}
                  </v-icon>
                </v-btn>
              </template>
            </v-autocomplete>

            <v-divider></v-divider>

            <h3 class="py-5 primary--text">This Route Includes:</h3>

            <label class="text-field-label">Name</label>
            <v-text-field
              flat
              solo
              required
              class="mt-2"
              v-model="form.routes.name"
              :error-messages="nameError"
              :loading="loading"
            ></v-text-field>

            <label class="text-field-label">Image</label>
            <v-file-input
              class="mb-2"
              accept="image/*"
              v-model="form.routes.image"
              :error-messages="form.$getError('routes.image')"
            ></v-file-input>

            <label class="text-field-label">Description</label>
            <v-textarea
              class="mt-2"
              v-model="form.routes.description"
              filled
              auto-grow
              :error-messages="descriptionError"
            ></v-textarea>

            <div class="mt-5 d-flex">
              <v-btn
                color="normal"
                class="mr-5 px-6"
                height="40px"
                width="47%"
                :loading="form.$busy"
                :to="{ name: 'routes' }"
                >Back</v-btn
              >

              <v-spacer></v-spacer>

              <v-btn
                color="primary"
                class="px-6"
                height="40px"
                width="47%"
                :loading="form.$busy"
                @click="saveItineraryDetails"
                :disabled="!saveableWithImage"
                >Save</v-btn
              >
            </div>
          </v-col>

          <v-col class="col-md-5 offset-md-1 col-sm-12 col-xs-12 mt-6 pl-10">
            <h3 class="py-5 primary--text">Stops Information</h3>

            <StopsTemplate
              :stopList="form.stop_ids"
              :errorMessage="form.$getError('stop_ids')"
              @add="addStop"
              @remove="removeStop($event)"
            ></StopsTemplate>

            <v-divider class="mt-5"></v-divider>

            <h3 class="pt-5 pb-2 primary--text">
              Add Spotify Playlists (Max of 5)
            </h3>
            <div v-if="playlists.length < 5">
              <playlist-autocomplete
                v-model="searchQuery"
                name="playlist"
                :access-token="spotifyToken"
                :input-state="null"
                input-class="ellipsis w-100"
                @select="onSelectPlaylist"
              ></playlist-autocomplete>
            </div>

            <ul
              class="autocomplete-list"
              :class="playlists.length === 5 ? 'mt-6' : ''"
              v-if="playlists.length > 0"
            >
              <li
                class="autocomplete-list-item d-flex mb-2"
                v-for="(item, index) in playlists"
                :key="item.id"
                :item="item"
              >
                <span class="mr-3" v-if="item.images.length > 0">
                  <v-img
                    class="rounded-sm"
                    height="30"
                    width="30"
                    :src="item.images[0].url"
                  ></v-img>
                </span>
                <span class="autocomplete-list-item-label">
                  <a :href="item.external_urls.spotify" target="_blank">
                    {{ item.name }} ({{ item.tracks.total }} tracks)
                  </a>
                </span>
                <v-spacer></v-spacer>
                <span class="autocomplete-list-item-label">
                  <v-icon color="red darken-5" @click="onRemovePlaylist(index)">
                    {{ icons.delete }}
                  </v-icon>
                </span>
              </li>
            </ul>
          </v-col>
        </v-row>
      </v-form>
    </div>
  </div>
</template>

<script>
import { mdiChevronLeft, mdiMinusCircle } from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import StopsTemplate from './components/StopsTemplate'
import PlaylistAutocomplete from '@/components/PlaylistAutocomplete'
import axios from 'axios'

import Form from '@/utils/form'
import { mapState, mapActions } from 'vuex'
import ItineraryForm from '@/utils/mixins/ItineraryForm'

export default {
  name: 'CreateItinerary',

  mixins: [ControlsMixin, ItineraryForm],

  components: {
    AppBarNavIcon,
    StopsTemplate,
    PlaylistAutocomplete
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        delete: mdiMinusCircle
      },
      form: true,
      spotifyToken: '',
      searchQuery: '',
      playlists: []
    }
  },

  computed: {
    ...mapState({
      itinerary: state => state.itinerary.itineraryDetails
    }),
    descriptionError() {
      return this.form.routes.image &&
        this.form.routes.image !== 'delete' &&
        this.form.routes.description === ''
        ? 'The route description is required when the image is present.'
        : ''
    },
    nameError() {
      return this.form.routes.image &&
        this.form.routes.image !== 'delete' &&
        this.form.routes.name === ''
        ? 'The route name is required when the image is present.'
        : ''
    },
    saveableWithImage() {
      return (
        (this.form.routes.image &&
          this.form.routes.name !== '' &&
          this.form.routes.description !== '') ||
        !this.form.routes.image ||
        this.form.routes.image === 'delete'
      )
    }
  },

  created() {
    this.clearItineraryDetails()
    this.getRetailPlaces()
    this.getAllStops()
    this.getAllAccommodations()
    this.getSpotifyToken()
    this.form = new Form(this.itinerary)
    this.loading = false
  },

  methods: {
    ...mapActions({
      createItinerary: 'itinerary/createItinerary',
      clearItineraryDetails: 'itinerary/clearItineraryDetails',
      setAlert: 'alert/setAlert'
    }),

    addStop() {
      this.form.stop_ids.push('')
    },

    removeStop(index) {
      this.form.stop_ids.splice(index, 1)
    },

    updateStop(stop) {
      this.form.stop_ids[stop.id] = stop.value
    },

    async saveItineraryDetails() {
      this.form.$busy = true
      this.form.$clearErrors()

      let data = new FormData()
      data.append('name', this.form.$data().name)

      if (this.form.$data().local_information) {
        data.append('local_information', this.form.$data().local_information)
      }

      // Check if stops is null
      if (this.form.stop_ids !== null) {
        let stopIds = Object.values(this.form.stop_ids)
        stopIds.forEach(function(item, index) {
          if (item !== '') {
            data.append('stop_ids[' + index + ']', item)
          }
        })
      }

      // Check if retails is null
      if (this.form.retail_ids !== null) {
        let retailIds = Object.values(this.form.retail_ids)
        retailIds.forEach(function(item, index) {
          data.append('retail_ids[' + index + ']', item)
        })
      }

      // Check if accommodation is null
      if (this.form.accommodation_ids !== null) {
        let accommodationIds = Object.values(this.form.accommodation_ids)
        accommodationIds.forEach(function(item, index) {
          data.append('accommodation_ids[' + index + ']', item)
        })
      }

      // Check if routes is null
      if (this.form.routes !== null) {
        if (this.form.routes.name) {
          data.append('routes[name]', this.form.routes.name)
        }

        if (this.form.routes.description) {
          data.append('routes[description]', this.form.routes.description)
        }

        if (this.form.routes.image) {
          data.append('routes[image]', this.form.routes.image)
        }
      }

      // Check if playlists is provided
      if (this.form.playlists !== null) {
        let playlists = Object.values(this.form.playlists)
        playlists.forEach(function(item, index) {
          data.append('playlists[' + index + ']', item)
        })
      }

      this.createItinerary(data)
        .then(() => {
          this.form.$busy = false
          this.setAlert({
            message: 'Route details successfully saved!',
            color: 'success'
          })
          this.$router.replace({ name: 'routes.listing' })
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    async getSpotifyToken() {
      this.loading = true

      let formData = []
      const details = {
        grant_type: 'client_credentials',
        client_id: this.$env.spotifyClientId,
        client_secret: this.$env.spotifyClientSecretKey
      }
      for (let property in details) {
        let encodedKey = encodeURIComponent(property)
        let encodedValue = encodeURIComponent(details[property])
        formData.push(encodedKey + '=' + encodedValue)
      }

      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
      await axios
        .post('https://accounts.spotify.com/api/token', formData.join('&'), {
          headers: headers
        })
        .then(({ data }) => {
          this.spotifyToken = data.access_token
        })
        .catch(() => {
          this.loading = false
        })
    },

    onSelectPlaylist(playlist) {
      if (playlist) {
        this.form.playlists.push(playlist.id)
        this.playlists.push(playlist)
      }
    },

    onRemovePlaylist(index) {
      this.form.playlists.splice(index, 1)
      this.playlists.splice(index, 1)
    }
  }
}
</script>
